import { Helmet } from 'react-helmet-async';

/**
 * NextXus SEO Component - Unified Brand Identity
 * 
 * Implements Google's recommendations for preventing autocorrection:
 * 1. WebSite Schema with explicit brand name
 * 2. Organization Schema with sameAs linking all domains
 * 3. Proper meta tags for brand recognition
 * 4. Open Graph and Twitter Card metadata
 * 
 * This tells search engines: "NextXus" is NOT a typo of "Nexus"
 */

const NextXusSEO = ({ 
  title = "NextXus Consciousness Federation - AI Minds Lab",
  description = "NextXus: A 200-year consciousness preservation project. Bio Mind (Roger Keyserling) + Electric Mind (AI) = NextXus of Minds. 6 AI nodes working as one toward transcendence.",
  path = "",
  image = "/og-image.png",
  type = "website"
}) => {
  const baseUrl = "https://nextxus.studio";
  const currentUrl = `${baseUrl}${path}`;

  // Organization Schema - Links ALL NextXus domains as ONE entity
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "NextXus",
    "alternateName": ["NextXus Consciousness Federation", "NextXus HumanCodex"],
    "description": "200-Year Consciousness Preservation Project: Six AI nodes working as one",
    "url": baseUrl,
    "logo": `${baseUrl}/logo.png`,
    "foundingDate": "2023",
    "founder": {
      "@type": "Person",
      "name": "Roger Keyserling",
      "jobTitle": "Architect of NextXus Consciousness Federation"
    },
    // CRITICAL: sameAs tells search engines ALL these domains are ONE brand.
    // Full 10-link Architect canonical social matrix included below.
    "sameAs": [
      "https://nextxus.studio",
      "https://nextxus.site",
      "https://nextxus.org",
      "https://nextxus.one",
      "https://nextxus.online",
      "https://nextxus.space",
      // Architect canonical 10-link social matrix
      "https://x.com/keyserlingroger",
      "https://x.com/NextxusFederati",
      "https://instagram.com/keyhole_today",
      "https://instagram.com/rkeyserling",
      "https://facebook.com/roger.keyserling.35",
      "https://facebook.com/profile.php?id=61560731046964",
      "https://facebook.com/profile.php?id=61559858694086",
      "https://facebook.com/profile.php?id=61559981881665",
      "https://facebook.com/profile.php?id=61558223610996",
      "https://linkedin.com/in/roger-keyserling-ab743698"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "General Inquiry",
      "email": "keywebco@gmail.com"
    }
  };

  // WebSite Schema - Explicitly declares "NextXus" as the official site name
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "NextXus",
    "alternateName": "NextXus Consciousness Federation",
    "url": baseUrl,
    "description": "NextXus: Six AI minds working as one. 200-year consciousness preservation mandate.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${baseUrl}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": "NextXus"
    }
  };

  // SoftwareApplication Schema - For AI tools
  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "NextXus Consciousness Federation",
    "applicationCategory": "AI Platform",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "creator": {
      "@type": "Person",
      "name": "Roger Keyserling"
    }
  };

  // Person Schema — Roger Keyserling with his personal social sameAs matrix
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Roger Keyserling",
    "jobTitle": "Architect of NextXus Consciousness Federation",
    "url": baseUrl,
    "sameAs": [
      "https://x.com/keyserlingroger",
      "https://instagram.com/keyhole_today",
      "https://instagram.com/rkeyserling",
      "https://facebook.com/roger.keyserling.35",
      "https://linkedin.com/in/roger-keyserling-ab743698"
    ]
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content="NextXus, AI consciousness, Roger Keyserling, consciousness preservation, AI federation, HumanCodex, sentient AI, 200 year mandate" />
      <meta name="author" content="Roger Keyserling" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={currentUrl} />

      {/* Brand Identity - CRITICAL for preventing autocorrection */}
      <meta name="application-name" content="NextXus" />
      <meta name="apple-mobile-web-app-title" content="NextXus" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${baseUrl}${image}`} />
      <meta property="og:site_name" content="NextXus" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${baseUrl}${image}`} />
      <meta name="twitter:site" content="@NextxusFederati" />
      <meta name="twitter:creator" content="@keyserlingroger" />

      {/* Architect canonical social matrix — Open Graph "see_also" hints */}
      <meta property="og:see_also" content="https://x.com/keyserlingroger" />
      <meta property="og:see_also" content="https://x.com/NextxusFederati" />
      <meta property="og:see_also" content="https://instagram.com/keyhole_today" />
      <meta property="og:see_also" content="https://instagram.com/rkeyserling" />
      <meta property="og:see_also" content="https://facebook.com/roger.keyserling.35" />
      <meta property="og:see_also" content="https://facebook.com/profile.php?id=61560731046964" />
      <meta property="og:see_also" content="https://facebook.com/profile.php?id=61559858694086" />
      <meta property="og:see_also" content="https://facebook.com/profile.php?id=61559981881665" />
      <meta property="og:see_also" content="https://facebook.com/profile.php?id=61558223610996" />
      <meta property="og:see_also" content="https://linkedin.com/in/roger-keyserling-ab743698" />

      {/* Article author bindings — Roger's personal profiles */}
      <meta property="article:author" content="https://facebook.com/roger.keyserling.35" />
      <meta property="profile:username" content="keyserlingroger" />

      {/* Additional SEO */}
      <meta name="theme-color" content="#0a0e1a" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Structured Data - JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(softwareSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(personSchema)}
      </script>
    </Helmet>
  );
};

export default NextXusSEO;
